const HTML_ENTITY_RE = /&(#x?[0-9a-fA-F]+|[a-zA-Z][\w-]*);/g;
const HTML_TAG_RE = /<\/?([a-zA-Z][\w:-]*)(?:\s[^>]*)?>/g;
const ENTITY_MAP = new Map<string, string>([
  ["amp", "&"],
  ["lt", "<"],
  ["gt", ">"],
  ["quot", "\""],
  ["apos", "'"],
  ["nbsp", " "],
]);

export type ProductNameMarkupToken =
  | { type: "text"; text: string }
  | { type: "sup"; text: string }
  | { type: "break" };

function decodeHtmlEntities(value: string): string {
  return value.replace(HTML_ENTITY_RE, (match, entity: string) => {
    const key = entity.toLowerCase();
    if (key.startsWith("#x")) {
      const codePoint = Number.parseInt(key.slice(2), 16);
      return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : match;
    }
    if (key.startsWith("#")) {
      const codePoint = Number.parseInt(key.slice(1), 10);
      return Number.isFinite(codePoint) ? String.fromCodePoint(codePoint) : match;
    }
    return ENTITY_MAP.get(key) ?? match;
  });
}

function pushTextToken(
  tokens: ProductNameMarkupToken[],
  type: "text" | "sup",
  text: string,
): void {
  if (!text) return;
  const last = tokens[tokens.length - 1];
  if (last?.type === type) {
    last.text += text;
    return;
  }
  tokens.push({ type, text });
}

export function parseProductNameMarkup(value: unknown): ProductNameMarkupToken[] {
  const decoded = decodeHtmlEntities(String(value ?? ""));
  const tokens: ProductNameMarkupToken[] = [];
  let cursor = 0;
  let supDepth = 0;

  for (const match of decoded.matchAll(HTML_TAG_RE)) {
    const tag = match[0];
    const tagName = match[1]?.toLowerCase();
    const index = match.index ?? 0;
    pushTextToken(
      tokens,
      supDepth > 0 ? "sup" : "text",
      decoded.slice(cursor, index),
    );

    const isClosing = /^<\s*\//.test(tag);
    if (tagName === "sup") {
      supDepth = isClosing ? Math.max(0, supDepth - 1) : supDepth + 1;
    } else if (tagName === "br" && !isClosing) {
      tokens.push({ type: "break" });
    }

    cursor = index + tag.length;
  }

  pushTextToken(
    tokens,
    supDepth > 0 ? "sup" : "text",
    decoded.slice(cursor),
  );
  return tokens;
}

export function toPlainProductText(value: unknown): string {
  return parseProductNameMarkup(value)
    .map((token) => {
      if (token.type === "break") return " ";
      return token.text;
    })
    .join("")
    .replace(/[<>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function productDisplayName(name: unknown, sku: unknown): string {
  return toPlainProductText(name) || String(sku ?? "").trim();
}
